import type {
  DealResourcePlan,
  ResourcingOutput,
  ResourcingOutputPhase,
  StaffingPlanEntry,
} from '@protoss/shared';
import { formatCurrencyAmount, readRolePricing, resolveRoleCost } from '@protoss/shared';
import { CalendarIcon, FileTextIcon, SkeletonBlock, Tooltip } from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { formatDateTimeWithYear } from '../../../lib/datetime';
import { ResourceInvestmentTable } from './ResourceInvestmentTable';

export interface DealResourcePlanPaneProps {
  plan: DealResourcePlan | null;
  loading: boolean;
  error: string | null;
  /**
   * Pricing-basis and discount editing is centralized in the Estimate pane, so
   * the resource plan table renders these read-only. These props remain only to
   * display the effective discount.
   */
  discountPercentageOverride?: number | null;
  defaultDiscountPercentage?: number | null;
  onRetry?: () => void;
  /**
   * Strict category gating: `false` when the deal's category has no matching
   * resource-plan profile, so the pane shows a "not configured" state and
   * generation is disabled. `undefined`/`true` = available (default).
   */
  available?: boolean;
}

type DisplayPhase = {
  key: string;
  label: string;
  weeks: number;
  startWeek: number;
  /** End-of-phase milestone label declared by the plan output, if any. */
  milestone?: string;
};

/**
 * Canonical size_tiered phase keys that have dedicated `[data-phase='…']` CSS
 * colors. Custom (flat_percentage) keys fall back to an index-based palette via
 * `data-phase-index`; gating on this set keeps the two from colliding.
 */
const KNOWN_PHASE_KEYS = new Set([
  'discovery_design',
  'discovery',
  'design',
  'build',
  'test',
  'deploy',
  'hypercare',
]);

/** Color attribute for a phase: known keys keep `data-phase`; custom keys add an index. */
function phaseIndexAttr(key: string, idx: number): { 'data-phase-index'?: number } {
  return KNOWN_PHASE_KEYS.has(key) ? {} : { 'data-phase-index': idx % 6 };
}

/**
 * Build the ordered display timeline from the plan's dynamic phases. Phase-set
 * agnostic: it reads each phase's engine-computed `start_week` (so phases may
 * overlap), merges any run of phases sharing a `display_group` into one bar
 * spanning min(start)→max(end), guarantees a minimum of 1 week so each segment
 * stays visible, and surfaces end-of-phase milestone markers.
 */
export function computeDisplayPhases(snap: ResourcingOutput): DisplayPhase[] {
  const phases = Array.isArray(snap.phases) ? snap.phases : [];

  // Legacy fallback: snapshots written before the engine scheduler lack
  // start_week. Reconstruct the old cumulative cursor so historical plans
  // render identically.
  const anyMissingStart = phases.some((p) => typeof p.start_week !== 'number');
  let startWeekOf: (p: ResourcingOutputPhase) => number;
  if (anyMissingStart) {
    const map = new Map<string, number>();
    let cursor = 1;
    for (const p of phases) {
      map.set(p.key, cursor);
      cursor += Math.max(p.weeks, 1);
    }
    startWeekOf = (p) => map.get(p.key) ?? 1;
  } else {
    startWeekOf = (p) => p.start_week;
  }

  const result: DisplayPhase[] = [];
  let i = 0;
  while (i < phases.length) {
    const phase = phases[i]!;
    if (phase.display_group) {
      // Collect the contiguous run of phases sharing this display group and
      // span it from the earliest start to the latest end (= sum for
      // contiguous waterfall, correct for overlap).
      const group = phase.display_group;
      let minStart = Infinity;
      let maxEnd = -Infinity; // exclusive end = start + weeks
      let milestone: string | undefined;
      let j = i;
      while (j < phases.length && phases[j]!.display_group === group) {
        const member = phases[j]!;
        const s = startWeekOf(member);
        const w = Math.max(member.weeks, 1);
        minStart = Math.min(minStart, s);
        maxEnd = Math.max(maxEnd, s + w);
        if (member.milestone_label) milestone = member.milestone_label;
        j++;
      }
      result.push({
        key: group,
        label: phase.display_group_label ?? phase.label,
        weeks: Math.max(maxEnd - minStart, 1),
        startWeek: minStart,
        ...(milestone ? { milestone } : {}),
      });
      i = j;
    } else {
      result.push({
        key: phase.key,
        label: phase.label,
        weeks: Math.max(phase.weeks, 1),
        startWeek: startWeekOf(phase),
        ...(phase.milestone_label ? { milestone: phase.milestone_label } : {}),
      });
      i++;
    }
  }

  return result;
}

function formatProjectSize(size: string): string {
  if (size === 'small') return 'Small';
  if (size === 'medium') return 'Medium';
  if (size === 'large') return 'Large';
  return size;
}

function getRoleCost(plan: DealResourcePlan, entry: StaffingPlanEntry): number {
  const { basis, offshorePercent } = readRolePricing(plan.rolePricingBasisByRoleId, entry.role_id);
  return resolveRoleCost(entry, basis, offshorePercent);
}

function getDisplayedTotalCost(plan: DealResourcePlan): number {
  return plan.resourcingSnapshot.staffing_plan.reduce(
    (sum, entry) => sum + getRoleCost(plan, entry),
    0
  );
}

export function DealResourcePlanPane({
  plan,
  loading,
  error,
  discountPercentageOverride,
  defaultDiscountPercentage,
  onRetry,
  available,
}: DealResourcePlanPaneProps) {
  if (available === false) {
    return (
      <div className="deal-resource-plan-pane__empty deal-resource-plan-pane__empty--neutral">
        <div className="deal-resource-plan-pane__empty-icon" aria-hidden="true">
          <FileTextIcon width={18} height={18} />
        </div>
        <div className="deal-resource-plan-pane__empty-copy">
          <h3 className="deal-resource-plan-pane__empty-title">
            Resource plan not configured for this category
          </h3>
          <p className="deal-resource-plan-pane__empty-body">
            This deal&rsquo;s category has no resource-plan configuration. Pick a configured
            category, or set one up for this category in Products.
          </p>
        </div>
      </div>
    );
  }

  if (loading && !plan) {
    return (
      <div
        className="deal-resource-plan-pane deal-resource-plan-pane--loading"
        aria-busy="true"
        aria-label="Loading resource plan"
      >
        {[0, 1, 2, 3, 4].map((item) => (
          <SkeletonBlock key={item} className="deal-resource-plan-pane__skeleton" />
        ))}
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div className="deal-resource-plan-pane__empty deal-resource-plan-pane__empty--danger">
        <div className="deal-resource-plan-pane__empty-icon" aria-hidden="true">
          <FileTextIcon width={18} height={18} />
        </div>
        <div className="deal-resource-plan-pane__empty-copy">
          <h3 className="deal-resource-plan-pane__empty-title">Resource plan unavailable</h3>
          {onRetry ? (
            <PageErrorState message={error} onRetry={onRetry} />
          ) : (
            <p className="deal-resource-plan-pane__empty-body">{error}</p>
          )}
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="deal-resource-plan-pane__empty deal-resource-plan-pane__empty--neutral">
        <div className="deal-resource-plan-pane__empty-icon" aria-hidden="true">
          <FileTextIcon width={18} height={18} />
        </div>
        <div className="deal-resource-plan-pane__empty-copy">
          <h3 className="deal-resource-plan-pane__empty-title">No resource plan yet</h3>
        </div>
      </div>
    );
  }

  if (plan.status === 'generating') {
    return (
      <div className="deal-resource-plan-pane deal-resource-plan-pane--generating">
        <p className="deal-resource-plan-pane__status-message">Generating resource plan…</p>
      </div>
    );
  }

  if (plan.status === 'failed') {
    return (
      <div className="deal-resource-plan-pane deal-resource-plan-pane--failed">
        <div className="deal-resource-plan-pane__empty deal-resource-plan-pane__empty--danger">
          <div className="deal-resource-plan-pane__empty-icon" aria-hidden="true">
            <FileTextIcon width={18} height={18} />
          </div>
          <div className="deal-resource-plan-pane__empty-copy">
            <h3 className="deal-resource-plan-pane__empty-title">Generation failed</h3>
            <p className="deal-resource-plan-pane__empty-body">
              {plan.errorMessage ?? 'An unexpected error occurred. Please try again.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const snap = plan.resourcingSnapshot;
  const displayPhases = computeDisplayPhases(snap);
  // Total span = the furthest end week across all (possibly overlapping) phases.
  // For waterfall this equals the sum of phase weeks (segments are contiguous);
  // for overlapping schedules it is the true calendar length.
  const displayTotalWeeks = displayPhases.reduce(
    (max, p) => Math.max(max, p.startWeek + p.weeks - 1),
    1
  );
  const weekNums = Array.from({ length: displayTotalWeeks }, (_, i) => i + 1);

  // The horizontal timeline bar is a sequential flex strip; it only renders
  // correctly for a contiguous waterfall. When phases overlap or leave gaps,
  // switch the bar to absolute positioning (like the Gantt) so it reflects the
  // real schedule. Waterfall plans stay on the untouched flex code path.
  const timelineHasGaps = displayPhases.some((p, idx) => {
    const expectedStart =
      idx === 0 ? 1 : displayPhases[idx - 1]!.startWeek + displayPhases[idx - 1]!.weeks;
    return p.startWeek !== expectedStart;
  });

  const showTimeline = snap.display?.show_timeline ?? true;
  const showGantt = snap.display?.show_gantt ?? true;

  const milestones = displayPhases
    .filter((p) => p.milestone)
    .map((p) => ({ label: p.milestone as string, week: p.startWeek + p.weeks - 1 }));

  const ganttStyle = { '--gantt-weeks': displayTotalWeeks } as React.CSSProperties;
  const displayedTotalCost = getDisplayedTotalCost(plan);

  return (
    <div className="deal-resource-plan-pane">
      <div className="deal-resource-plan-pane__meta">
        <div className="deal-resource-plan-pane__meta-left">
          <span className="deal-resource-plan-pane__meta-item">
            Generated {formatDateTimeWithYear(plan.createdAt)}
          </span>
        </div>
      </div>

      {error ? <p className="deal-resource-plan-pane__inline-error">{error}</p> : null}

      {/* ── Resource Allocation card ──────────────────────────────────────── */}
      <section className="deal-resource-plan-pane__card">
        <div className="deal-resource-plan-pane__card-header">
          <div>
            <h2 className="deal-resource-plan-pane__card-title">Resource Allocation</h2>
            <p className="deal-resource-plan-pane__card-subtitle">
              Recommended staffing plan based on project requirements
            </p>
          </div>
        </div>

        {/* Metrics */}
        <div className="deal-resource-plan-pane__metrics">
          {snap.project_size ? (
            <div className="deal-resource-plan-pane__metric">
              <span className="deal-resource-plan-pane__metric-label">Project Size</span>
              <span className="deal-resource-plan-pane__metric-value">
                {formatProjectSize(snap.project_size)}
              </span>
            </div>
          ) : null}
          {snap.distribution_model !== 'ai' && snap.timeline_weeks > 0 ? (
            <div className="deal-resource-plan-pane__metric">
              <span className="deal-resource-plan-pane__metric-label">Timeline</span>
              <span className="deal-resource-plan-pane__metric-value">
                {snap.timeline_weeks} weeks
              </span>
            </div>
          ) : null}
          <div className="deal-resource-plan-pane__metric">
            <span className="deal-resource-plan-pane__metric-label">Total Hours</span>
            <span className="deal-resource-plan-pane__metric-value deal-resource-plan-pane__metric-value--accent">
              {snap.totals.total_hours.toLocaleString()} hrs
            </span>
          </div>
          <div className="deal-resource-plan-pane__metric">
            <span className="deal-resource-plan-pane__metric-label">Est. Cost</span>
            <span className="deal-resource-plan-pane__metric-value">
              ${formatCurrencyAmount(displayedTotalCost)}
            </span>
          </div>
        </div>

        {/* Phase timeline */}
        {showTimeline && (
          <div className="deal-resource-plan-pane__timeline">
            <h3 className="deal-resource-plan-pane__timeline-title">
              <CalendarIcon width={14} height={14} aria-hidden="true" />
              Project Timeline
            </h3>

            <div
              className={`deal-resource-plan-pane__bar${
                timelineHasGaps ? ' deal-resource-plan-pane__bar--overlap' : ''
              }`}
            >
              {displayPhases.map((phase, idx) => (
                <Tooltip.Root key={phase.key}>
                  <Tooltip.Trigger asChild>
                    <div
                      className="deal-resource-plan-pane__bar-segment"
                      data-phase={phase.key}
                      {...phaseIndexAttr(phase.key, idx)}
                      style={
                        timelineHasGaps
                          ? {
                              position: 'absolute',
                              left: `${((phase.startWeek - 1) / displayTotalWeeks) * 100}%`,
                              width: `${(phase.weeks / displayTotalWeeks) * 100}%`,
                            }
                          : { width: `${(phase.weeks / displayTotalWeeks) * 100}%` }
                      }
                    >
                      {phase.weeks > 2 && (
                        <span className="deal-resource-plan-pane__bar-label">{phase.label}</span>
                      )}
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
                      {phase.label}: Wk {phase.startWeek}–{phase.startWeek + phase.weeks - 1}
                      <Tooltip.Arrow className="ui-tooltip-arrow" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              ))}
            </div>

            <div className="deal-resource-plan-pane__milestones">
              <span className="deal-resource-plan-pane__week-edge deal-resource-plan-pane__week-edge--start">
                Week 1
              </span>
              <span className="deal-resource-plan-pane__week-edge deal-resource-plan-pane__week-edge--end">
                Week {displayTotalWeeks}
              </span>
              {milestones.map((milestone) => (
                <div
                  key={milestone.label}
                  className="deal-resource-plan-pane__milestone"
                  style={{ left: `${(milestone.week / displayTotalWeeks) * 100}%` }}
                >
                  <div className="deal-resource-plan-pane__milestone-dot" aria-hidden="true" />
                  <span className="deal-resource-plan-pane__milestone-label">
                    {milestone.label} (Wk {milestone.week})
                  </span>
                </div>
              ))}
            </div>

            {/* Vertical timeline - shown only at narrow widths via container query */}
            <div className="deal-resource-plan-pane__timeline-v" aria-hidden="true">
              {displayPhases.map((phase, idx) => (
                <div key={phase.key} className="deal-resource-plan-pane__timeline-v-row">
                  <div className="deal-resource-plan-pane__timeline-v-bar-track">
                    <div
                      className="deal-resource-plan-pane__timeline-v-bar"
                      data-phase={phase.key}
                      {...phaseIndexAttr(phase.key, idx)}
                      style={{ width: `${(phase.weeks / displayTotalWeeks) * 100}%` }}
                    />
                  </div>
                  <div className="deal-resource-plan-pane__timeline-v-info">
                    <span className="deal-resource-plan-pane__timeline-v-label">{phase.label}</span>
                    <span className="deal-resource-plan-pane__timeline-v-range">
                      Wk {phase.startWeek}–{phase.startWeek + phase.weeks - 1} · {phase.weeks}w
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Staffing table */}
        <ResourceInvestmentTable
          plan={plan}
          discountPercentageOverride={discountPercentageOverride}
          defaultDiscountPercentage={defaultDiscountPercentage}
        />
      </section>

      {/* ── Gantt chart card ─────────────────────────────────────────────── */}
      {showGantt && (
        <section className="deal-resource-plan-pane__gantt">
          <h3 className="deal-resource-plan-pane__gantt-title">Project Gantt Chart</h3>

          <div className="deal-resource-plan-pane__gantt-body">
            {/* Fixed labels column - outside scroll so scrollbar starts after it */}
            <div className="deal-resource-plan-pane__gantt-labels">
              <div
                className="deal-resource-plan-pane__gantt-row-label deal-resource-plan-pane__gantt-row-label--header"
                aria-hidden="true"
              />
              {displayPhases.map((phase) => (
                <div key={phase.key} className="deal-resource-plan-pane__gantt-row-label">
                  <span className="deal-resource-plan-pane__gantt-phase-name">{phase.label}</span>
                  <span className="deal-resource-plan-pane__gantt-phase-range">
                    Wk {phase.startWeek} – {phase.startWeek + phase.weeks - 1}
                  </span>
                </div>
              ))}
              <div className="deal-resource-plan-pane__gantt-row-label">
                <span className="deal-resource-plan-pane__gantt-phase-name">Milestones</span>
              </div>
            </div>

            {/* Scrollable tracks - scrollbar only spans this column */}
            <div className="deal-resource-plan-pane__gantt-scroll">
              <div className="deal-resource-plan-pane__gantt-grid" style={ganttStyle}>
                {/* Week header row */}
                <div className="deal-resource-plan-pane__gantt-row deal-resource-plan-pane__gantt-row--header">
                  <div className="deal-resource-plan-pane__gantt-track">
                    {weekNums.map((w) => (
                      <div key={w} className="deal-resource-plan-pane__gantt-week-cell">
                        W{w}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Phase track rows */}
                {displayPhases.map((phase, idx) => (
                  <div key={phase.key} className="deal-resource-plan-pane__gantt-row">
                    <div className="deal-resource-plan-pane__gantt-track deal-resource-plan-pane__gantt-track--body">
                      {weekNums.map((w) => (
                        <div
                          key={w}
                          className="deal-resource-plan-pane__gantt-week-cell deal-resource-plan-pane__gantt-week-cell--body"
                        />
                      ))}
                      <div
                        className="deal-resource-plan-pane__gantt-bar"
                        data-phase={phase.key}
                        {...phaseIndexAttr(phase.key, idx)}
                        style={{
                          left: `${((phase.startWeek - 1) / displayTotalWeeks) * 100}%`,
                          width: `${(phase.weeks / displayTotalWeeks) * 100}%`,
                        }}
                      >
                        {phase.weeks > 1 ? `${phase.weeks}w` : null}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Milestone track row */}
                <div className="deal-resource-plan-pane__gantt-row">
                  <div className="deal-resource-plan-pane__gantt-track deal-resource-plan-pane__gantt-track--body">
                    {weekNums.map((w) => (
                      <div
                        key={w}
                        className="deal-resource-plan-pane__gantt-week-cell deal-resource-plan-pane__gantt-week-cell--body"
                      />
                    ))}
                    {milestones.map((milestone) => (
                      <Tooltip.Root key={milestone.label}>
                        <Tooltip.Trigger asChild>
                          <div
                            className="deal-resource-plan-pane__gantt-milestone-dot"
                            style={{ left: `${(milestone.week / displayTotalWeeks) * 100}%` }}
                            aria-label={milestone.label}
                          />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
                            {milestone.label}
                            <Tooltip.Arrow className="ui-tooltip-arrow" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="deal-resource-plan-pane__gantt-legend">
            {displayPhases.map((phase, idx) => (
              <div key={phase.key} className="deal-resource-plan-pane__gantt-legend-item">
                <span
                  className="deal-resource-plan-pane__gantt-legend-swatch"
                  data-phase={phase.key}
                  {...phaseIndexAttr(phase.key, idx)}
                  aria-hidden="true"
                />
                <span className="deal-resource-plan-pane__gantt-legend-label">{phase.label}</span>
              </div>
            ))}
            {milestones.length > 0 && (
              <div className="deal-resource-plan-pane__gantt-legend-item">
                <span
                  className="deal-resource-plan-pane__gantt-legend-swatch deal-resource-plan-pane__gantt-legend-swatch--milestone"
                  aria-hidden="true"
                />
                <span className="deal-resource-plan-pane__gantt-legend-label">Milestone</span>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
